import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import { ListDeleteOnly, ListEditDeleteActions } from '../../../components/admin/ListRowActions';
import '../quotes/quote-generator.css';
import { apiRequest } from '../../../api/client';

interface InvoiceListItem {
    _id: string;
    serialNumber?: number;
    contact?: {
        name: string;
    };
    invoiceNumber?: string;
    status: string;
    createdAt: string;
    updatedAt?: string;
    total?: number;
    meta?: {
        invoiceTitle?: string;
    };
}

export default function InvoicesList(): React.JSX.Element {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        apiRequest<{ data: any[] }>('/api/invoices')
            .then((data) => {
                setErrorMessage(null);
                setInvoices(Array.isArray(data?.data) ? data.data : []);
            })
            .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : 'Failed to load invoices.';
                setErrorMessage(message);
                setInvoices([]);
            });
    }, []);

    const handleDelete = (id: string) => {
        if (
            !window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')
        ) {
            return;
        }
        apiRequest(`/api/invoices/${id}`, { method: 'DELETE' })
            .then(() => {
                setInvoices((prev) => prev.filter((inv) => inv._id !== id));
                setErrorMessage(null);
            })
            .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : 'Failed to delete invoice.';
                setErrorMessage(message);
            });
    };

    const safeInvoices = Array.isArray(invoices) ? invoices : [];
    const draftInvoices = safeInvoices.filter((inv) => inv.status === 'draft');
    const historyInvoices = safeInvoices.filter((inv) => inv.status !== 'draft');

    const fmtCurrency = (val: number) => `$${val.toFixed(2)}`;

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: '1000px' }}>
                    {errorMessage && (
                        <div className="banner banner-error" style={{ marginBottom: '16px' }}>
                            {errorMessage}
                        </div>
                    )}
                    <header className="quote-header" style={{ marginBottom: '32px' }}>
                        <div>
                            <h1 style={{ fontSize: '2.5rem', marginBottom: '8px' }}>Invoices</h1>
                            <p style={{ color: 'var(--dash-muted)', margin: 0, fontSize: '1.05rem' }}>
                                Track drafts, send invoices, and manage history.
                            </p>
                        </div>
                        <button
                            className="btn-primary"
                            style={{ padding: '12px 24px', fontSize: '0.95rem', boxShadow: '0 4px 14px rgba(46, 138, 87, 0.3)' }}
                            onClick={() => navigate('/admin/invoice/new')}
                        >
                            + Create New Invoice
                        </button>
                    </header>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                        {draftInvoices.length > 0 && (
                            <section>
                                <div
                                    className="quote-section-title"
                                    style={{ border: 'none', fontSize: '1.25rem', marginBottom: '16px' }}
                                >
                                    Drafts ({draftInvoices.length})
                                </div>
                                <div className="quote-card" style={{ padding: 0, overflow: 'hidden' }}>
                                    <div className="list-responsive">
                                        <div className="list-table-pane quote-table-container">
                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Invoice #</th>
                                                        <th>Last Edited</th>
                                                        <th>Client</th>
                                                        <th>Title</th>
                                                        <th>Status</th>
                                                        <th style={{ textAlign: 'right' }}>Total</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {draftInvoices.map((inv) => (
                                                        <tr
                                                            key={inv._id}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => navigate(`/admin/invoice/new?id=${inv._id}`)}
                                                        >
                                                            <td style={{ fontWeight: 600 }}>
                                                                #{typeof inv.serialNumber === 'number' ? inv.serialNumber : '—'}
                                                            </td>
                                                            <td style={{ color: 'var(--dash-muted)' }}>
                                                                {new Date(inv.updatedAt || inv.createdAt).toLocaleDateString()}
                                                            </td>
                                                            <td>{inv.contact?.name}</td>
                                                            <td>{inv.meta?.invoiceTitle || inv.invoiceNumber || 'Draft Invoice'}</td>
                                                            <td>
                                                                <span className="status-badge draft">Draft</span>
                                                            </td>
                                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                                                {fmtCurrency(inv.total || 0)}
                                                            </td>
                                                            <td style={{ textAlign: 'right' }}>
                                                                <ListEditDeleteActions
                                                                    onEdit={() => navigate(`/admin/invoice/new?id=${inv._id}`)}
                                                                    onDelete={() => handleDelete(inv._id)}
                                                                    editTitle="Edit invoice"
                                                                    deleteTitle="Delete invoice"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="list-cards-pane">
                                            {draftInvoices.map((inv) => (
                                                <article
                                                    key={`m-${inv._id}`}
                                                    className="list-land-card list-land-card--clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => navigate(`/admin/invoice/new?id=${inv._id}`)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            navigate(`/admin/invoice/new?id=${inv._id}`);
                                                        }
                                                    }}
                                                >
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Invoice #</span>
                                                        <span className="list-land-value">
                                                            #{typeof inv.serialNumber === 'number' ? inv.serialNumber : '—'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Total</span>
                                                        <span className="list-land-value">{fmtCurrency(inv.total || 0)}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Client</span>
                                                        <span className="list-land-value">{inv.contact?.name || '—'}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Title</span>
                                                        <span className="list-land-value">
                                                            {inv.meta?.invoiceTitle || inv.invoiceNumber || 'Draft Invoice'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Last edited</span>
                                                        <span className="list-land-value list-land-value--muted">
                                                            {new Date(inv.updatedAt || inv.createdAt).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Status</span>
                                                        <span className="list-land-value">
                                                            <span className="status-badge draft">Draft</span>
                                                        </span>
                                                    </div>
                                                    <div className="list-land-actions">
                                                        <ListEditDeleteActions
                                                            onEdit={() => navigate(`/admin/invoice/new?id=${inv._id}`)}
                                                            onDelete={() => handleDelete(inv._id)}
                                                            editTitle="Edit invoice"
                                                            deleteTitle="Delete invoice"
                                                        />
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}

                        <section>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '16px'
                                }}
                            >
                                <div
                                    className="quote-section-title"
                                    style={{ border: 'none', fontSize: '1.25rem', margin: 0 }}
                                >
                                    Sent History
                                </div>
                                {historyInvoices.length > 0 && (
                                    <span style={{ fontSize: '0.9rem', color: 'var(--dash-muted)' }}>
                                        {historyInvoices.length} record(s)
                                    </span>
                                )}
                            </div>

                            <div className="quote-card" style={{ padding: 0, overflow: 'hidden', minHeight: '200px' }}>
                                {historyInvoices.length === 0 ? (
                                    <div className="empty-state">
                                        <div className="empty-state-icon">📮</div>
                                        <h3>No History Yet</h3>
                                        <p>Invoices you send will appear here automatically.</p>
                                    </div>
                                ) : (
                                    <div className="list-responsive">
                                        <div className="list-table-pane quote-table-container">
                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Invoice #</th>
                                                        <th>Sent Date</th>
                                                        <th>Client</th>
                                                        <th>Title</th>
                                                        <th>Status</th>
                                                        <th style={{ textAlign: 'right' }}>Total</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historyInvoices.map((inv) => (
                                                        <tr key={inv._id}>
                                                            <td style={{ fontWeight: 600 }}>
                                                                #{typeof inv.serialNumber === 'number' ? inv.serialNumber : '—'}
                                                            </td>
                                                            <td style={{ color: 'var(--dash-muted)' }}>
                                                                {new Date(inv.updatedAt || inv.createdAt).toLocaleDateString()}
                                                            </td>
                                                            <td>{inv.contact?.name}</td>
                                                            <td>{inv.meta?.invoiceTitle || inv.invoiceNumber || 'Invoice'}</td>
                                                            <td>
                                                                <span className="status-badge sent">{inv.status}</span>
                                                            </td>
                                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                                                {fmtCurrency(inv.total || 0)}
                                                            </td>
                                                            <td style={{ textAlign: 'right' }}>
                                                                <ListDeleteOnly
                                                                    onDelete={() => handleDelete(inv._id)}
                                                                    deleteTitle="Delete invoice"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="list-cards-pane">
                                            {historyInvoices.map((inv) => (
                                                <article key={`m-${inv._id}`} className="list-land-card">
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Invoice #</span>
                                                        <span className="list-land-value">
                                                            #{typeof inv.serialNumber === 'number' ? inv.serialNumber : '—'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Total</span>
                                                        <span className="list-land-value">{fmtCurrency(inv.total || 0)}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Client</span>
                                                        <span className="list-land-value">{inv.contact?.name || '—'}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Title</span>
                                                        <span className="list-land-value">
                                                            {inv.meta?.invoiceTitle || inv.invoiceNumber || 'Invoice'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Sent</span>
                                                        <span className="list-land-value list-land-value--muted">
                                                            {new Date(inv.updatedAt || inv.createdAt).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Status</span>
                                                        <span className="list-land-value">
                                                            <span className="status-badge sent">{inv.status}</span>
                                                        </span>
                                                    </div>
                                                    <div className="list-land-actions">
                                                        <ListDeleteOnly
                                                            onDelete={() => handleDelete(inv._id)}
                                                            deleteTitle="Delete invoice"
                                                        />
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    <div style={{ marginTop: '40px', textAlign: 'center' }}>
                        <button
                            className="dash-btn dash-btn-ghost"
                            onClick={() => navigate('/admin/dashboard')}
                            style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}
                        >
                            ← Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
